import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../../constants/globalcss';
import { commonStyles, sizes } from '../../styles/commonStyles';
import FormButtonGroup from '../FormButtonGroup';
import { requestHelp, HelpRequestPayload } from '../../api/User.client';
import HelpFormFields from './HelpFormFields';
import HelpSuccessMessage from './HelpSuccessMessage';
import { useHelpForm } from './useHelpForm';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  moreDetails: string;
  selectedOption: string;
}

const helpOptions = [
  { label: 'Login Issues', value: 'login-issues' },
  { label: 'Account Creation', value: 'account-creation' },
  { label: 'Password Reset', value: 'password-reset' },
  { label: 'Technical Support', value: 'technical-support' },
  { label: 'Other', value: 'other' },
];

const initialFormData: FormData = {
  email: '',
  moreDetails: '',
  name: '',
  phone: '',
  selectedOption: '',
};

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Reset success state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowSuccess(false);
      setApiError(null);
    }
  }, [isOpen]);

  const validateEmail = (email: string) => {
    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address.';
    }
    if (email.trim().toLowerCase().endsWith('@gmail.com')) {
      return 'Gmail addresses are not accepted.';
    }
    return null;
  };

  const {
    formData,
    setFormData,
    emailError,
    setEmailError,
    handleInputChange,
    clearForm,
  } = useHelpForm(initialFormData, validateEmail);

  const isFormValid = () => {
    return (
      formData.name.trim() !== '' &&
      formData.email.trim() !== '' &&
      !emailError &&
      formData.selectedOption.trim() !== ''
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    const payload: HelpRequestPayload = {
      Email: formData.email,
      Name: formData.name,
      Note: formData.moreDetails,
      Phone: formData.phone,
      Type: formData.selectedOption,
    };

    try {
      await requestHelp(payload);
      setApiError(null);
      setShowSuccess(true);
      clearForm();
    } catch (error: any) {
      setApiError(
        error?.message ||
          'An error occurred while submitting your request. Please try again later.'
      );
      setShowSuccess(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: sizes.borderRadius.large,
          padding: sizes.spacing.md,
        },
      }}
    >
      {/* Hide header when success is shown */}
      {!showSuccess && (
        <DialogTitle
          sx={{
            ...commonStyles.sectionTitle,
            marginBottom: sizes.spacing.lg,
            paddingRight: sizes.spacing.xl,
          }}
        >
          <Typography
            variant="h5"
            component="div"
            sx={commonStyles.sectionTitle}
          >
            How can we help you?
          </Typography>
          {apiError && (
            <Typography
              variant="body2"
              sx={{
                color: colors.error,
                mt: sizes.spacing.sm,
              }}
            >
              {apiError}
            </Typography>
          )}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: colors.text.secondary,
              position: 'absolute',
              right: sizes.spacing.sm,
              top: sizes.spacing.sm,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      )}

      <DialogContent>
        {showSuccess ? (
          <HelpSuccessMessage onClose={onClose} />
        ) : (
          <HelpFormFields
            formData={formData}
            handleInputChange={handleInputChange}
            emailError={emailError}
            helpOptions={helpOptions}
            sizes={sizes}
            commonStyles={commonStyles}
          />
        )}
      </DialogContent>
      {!showSuccess && (
        <DialogActions sx={{ justifyContent: 'center' }}>
          <FormButtonGroup
            onClear={onClose}
            clearButtonText="Cancel"
            onSubmit={handleSubmit}
            submitting={false}
            disableSubmit={!isFormValid()}
            submitTooltip={
              !isFormValid()
                ? 'Please fill all required fields and fix errors before submitting.'
                : ''
            }
          />
        </DialogActions>
      )}
    </Dialog>
  );
};

export default HelpModal;
